import { Component, OnDestroy, OnInit, Input, SimpleChanges, OnChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GetSituFacadeResponse, RenderedOutputStatus, SituViewWebApiClient, GetSituFacadeRequest, HomeConfigFile } from 'situview-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { GetSituFacadeResponseExtended } from '../situ-facade/situ-facade.component';
import { SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';

@Component({
  selector: 'situ-facade-readonly',
  templateUrl: './situ-facade-readonly.component.html',
  styleUrls: ['./situ-facade-readonly.component.less']
})
export class SituFacadeReadonlyComponent implements OnInit, OnDestroy, OnChanges {
  @Input() routeParams: any;
  
  /* view high level controls */
  paramId: string | undefined;
  paramToId: string | undefined;
  initialised: boolean = false;

  /* view data */
  gotFacade: GetSituFacadeResponseExtended | undefined;
  secondryFacade: GetSituFacadeResponseExtended | undefined;
  selectedFacadeFile: string | undefined;
  readonly renderedOutputStatusEnum = RenderedOutputStatus;
  renderStatus: RenderedOutputStatus | undefined;
  linkToPortal: string | undefined;
  secondryLinkToPortal: string | undefined;
  viewerWidth: string = "100vw";
  viewerHeight: string = "100vh";
  displayOnly: boolean = false;
  connectType: string | undefined;
  connectUrl: string | undefined;

  constructor(
    private snackBar: MatSnackBar,
    private configService: ConfigService,
    private situViewClient: SituViewWebApiClient,
    private renderServiceClient: SituRenderServiceWebApiClient) {}

  ngOnInit(): void {
    this.processRouteParams();
  }

  processRouteParams() {
    if (this.routeParams['id'] && this.routeParams['to']) {
      this.paramId = this.routeParams['to'];
      this.paramToId = this.routeParams['id'];
    } else if (this.routeParams['id']) {
      this.paramId = this.routeParams['id'];
    }

    const heightoverride = this.routeParams['heightoverride'];
    if (heightoverride) {
      this.displayOnly = heightoverride !== "100vh";
      
      const numericHeight = parseFloat(heightoverride); // remove 'px' and convert to number
      const calculatedWidth = (4 / 3) * numericHeight;
    
      this.viewerWidth = `${calculatedWidth}px`;
      this.viewerHeight = heightoverride.toLowerCase();
    } else {
      this.displayOnly = this.routeParams['quickview'];
    }
  }

  ngOnDestroy(): void {
  }

  ngAfterViewInit() {
    if (this.paramId) {
      this.loadFacadeById(this.paramId, this.paramToId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.routeParams && !changes.routeParams.firstChange) {
      this.processRouteParams(); // Re-run the logic when routeParams changes
      if (this.paramId) {
        this.loadFacadeById(this.paramId, this.paramToId);
      }
    }
  }

  openInPortal(primaryLink: boolean = true) {
    if (primaryLink) {
      window.open(this.linkToPortal, '_blank');
    } else {
      window.open(this.secondryLinkToPortal, '_blank');
    }
  }

  loadComparisonFacadeById(renderedOutputId: string) {
    this.getSituFacadeById(renderedOutputId)
      .then((getFacadeResponse: GetSituFacadeResponse) => {
        if (getFacadeResponse?.success) {
          let extendedResponse = new GetSituFacadeResponseExtended(getFacadeResponse);
          extendedResponse.imageUrl = this.getImageUrl(extendedResponse?.artifactId!);
          this.secondryFacade = extendedResponse;
          if (getFacadeResponse?.artifactId && getFacadeResponse?.status === RenderedOutputStatus.Complete) {
            this.secondryLinkToPortal = `${this.configService.config.situPortalBaseUrl}/situ-facade?id=${getFacadeResponse.artifactId}`;
          }
        }
      });
  }

  loadFacadeById(renderedOutputId: string, comparisonRenderedOutputId: string | undefined = undefined, isPolling: boolean = false) {
    this.paramId = undefined;
    this.gotFacade = undefined;
    this.secondryFacade = undefined;

    this.getSituFacadeById(renderedOutputId)
      .then((getFacadeResponse: GetSituFacadeResponse) => {
        if (getFacadeResponse?.success && getFacadeResponse?.id) {
          if (getFacadeResponse?.artifactId && getFacadeResponse?.status === RenderedOutputStatus.Complete) {
            this.linkToPortal = `${this.configService.config.situPortalBaseUrl}/situ-facade?id=${getFacadeResponse.artifactId}`;
          }
          if (this.gotFacade?.id !== getFacadeResponse?.id || this.gotFacade?.status !== getFacadeResponse?.status) {
            let extendedResponse = new GetSituFacadeResponseExtended(getFacadeResponse);
            if (this.gotFacade?.id !== getFacadeResponse?.id) {
              extendedResponse.imageUrl = this.getImageUrl(extendedResponse?.artifactId!);
              this.gotFacade = extendedResponse;
            } else if (this.gotFacade && this.gotFacade?.status !== getFacadeResponse?.status) {
              this.gotFacade.status = getFacadeResponse.status;
            }

            if (!this.gotFacade?.status || !this.gotFacade?.id) return;

            if (!isPolling && ([RenderedOutputStatus.InProgress, RenderedOutputStatus.Queued].includes(this.gotFacade?.status))) {
              return;
            }

            if ([RenderedOutputStatus.Complete, RenderedOutputStatus.Failed].includes(this.gotFacade?.status)) {
              if (this.gotFacade.status === RenderedOutputStatus.Complete) {
                if (comparisonRenderedOutputId) {
                  this.loadComparisonFacadeById(comparisonRenderedOutputId);
                }
              }
            }
          }
        } else {
          this.renderServiceClient.getSituConnectType(renderedOutputId)
            .then((connectType: string) => {
              if (connectType) {
                this.connectType = connectType;
                this.connectUrl = this.getImageUrl(renderedOutputId!);;
              } else {
                this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
              }
            });
        }
      })
      .catch((err: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
      })
      .finally(() => {
      });
  }

  private getSituFacadeById(renderedOutputId: string): Promise<GetSituFacadeResponse> {
    let request = new GetSituFacadeRequest();
    request.renderedOutputId = renderedOutputId;

    return this.situViewClient.getSituFacade(request);
  }

  getLiveFacadeEmbedUrl() {
    if (this.gotFacade?.artifactId) {
      let url = `${this.configService.config.facadeEmbedBaseUrl}/embed.html?id=${this.gotFacade.artifactId}&products=1`;
      const file = this.effectiveFacadeFile;
      if (file) {
        url += `&file=${encodeURIComponent(file)}`;
      }
      // Idle hide timer for the product callouts, matching situVIEW: after 3
      // seconds without interaction the callouts fade away and any activity
      // brings them back. Always on here - this component only ever serves the
      // situLINKs we share ourselves. The embed code builders paste into their
      // own sites is built in SituFacadeComponent and leaves the param off.
      url += `&hideTimer=1`;
      return url;
    }
  }

  get facadeImageFiles(): string[] {
    return (this.gotFacade?.files ?? [])
      .map((f: HomeConfigFile) => f.fileName)
      .filter((name): name is string => !!name && /\.(jpe?g|png|webp)$/i.test(name));
  }

  get hasMultipleFacadeFiles(): boolean {
    return this.facadeImageFiles.length > 1;
  }

  get effectiveFacadeFile(): string | undefined {
    if (this.selectedFacadeFile && this.facadeImageFiles.includes(this.selectedFacadeFile)) {
      return this.selectedFacadeFile;
    }
    return this.facadeImageFiles[0];
  }

  prevFacadeFile() {
    const files = this.facadeImageFiles;
    if (files.length <= 1) return;
    const current = this.effectiveFacadeFile ?? files[0];
    const i = files.indexOf(current);
    this.selectedFacadeFile = files[(i <= 0 ? files.length : i) - 1];
  }

  nextFacadeFile() {
    const files = this.facadeImageFiles;
    if (files.length <= 1) return;
    const current = this.effectiveFacadeFile ?? files[0];
    const i = files.indexOf(current);
    this.selectedFacadeFile = files[(i + 1) % files.length];
  }

  private getImageUrl(artifactId: string, resolution?: number) {
    return resolution ? 
      `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=facade.jpg&width=${resolution}` :
      `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=facade.jpg`;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}